import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { fetchInsights } from "../lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const Insights = () => {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { fetchInsights().then(setPosts).catch(() => {}); }, []);

  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const filtered = filter === "All" ? posts : posts.filter((p) => p.category === filter);
  const featured = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p?.id !== featured?.id);

  return (
    <div data-testid="insights-page">
      <section className="border-b border-[var(--rule)] py-20 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500">— Vol. 04 · Journal</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-9">
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-[8.5vw] font-light tracking-tighter leading-[0.92]">
              Insights. <span className="italic">Carefully written.</span>
            </h1>
            <p className="font-serif text-xl lg:text-2xl text-neutral-700 mt-10 max-w-3xl leading-snug">
              Notes, opinions and unhurried analysis from the practice. No tax-update bulletins.
              No SEO copy. Only writing we'd want to read ourselves.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Category filter */}
      <section className="border-b border-[var(--rule)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-wrap gap-x-8 gap-y-3">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setFilter(c)}
              className={`text-[11px] tracking-[0.28em] uppercase editorial-link ${
                filter === c ? "active text-neutral-950" : "text-neutral-500"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="py-16 lg:py-24 border-b border-[var(--rule)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <Link
                to={`/insights/${featured.slug}`}
                data-testid="featured-post"
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 group"
              >
                <div className="lg:col-span-7 img-frame aspect-[16/10] border border-[var(--rule)]">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover grayscale" />
                </div>
                <div className="lg:col-span-5 flex flex-col justify-end">
                  <div className="flex gap-4 text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-4">
                    <span>Featured</span><span>·</span>
                    <span>{featured.category}</span><span>·</span>
                    <span>{formatDate(featured.date)}</span>
                  </div>
                  <h2 className="font-serif text-4xl lg:text-6xl font-light tracking-tight leading-[1.02] group-hover:italic transition-all">
                    {featured.title}
                  </h2>
                  <p className="text-base text-neutral-600 mt-6 leading-relaxed max-w-lg">{featured.excerpt}</p>
                  <p className="text-[11px] tracking-[0.28em] uppercase text-neutral-500 mt-6">{featured.read_time}</p>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-neutral-900">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`post-card-${p.slug}`}
                  className="group block border-b border-neutral-900 md:border-r md:[&:nth-child(3n)]:border-r-0 p-6 lg:p-8 h-full"
                >
                  <div className="img-frame aspect-[5/4] border border-[var(--rule)] mb-6">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover grayscale" />
                  </div>
                  <div className="flex gap-3 text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-3">
                    <span>{p.category}</span><span>·</span><span>{formatDate(p.date)}</span>
                  </div>
                  <h3 className="font-serif text-2xl lg:text-3xl font-light tracking-tight leading-snug group-hover:italic transition-all">
                    {p.title}
                  </h3>
                  <p className="text-sm text-neutral-600 mt-4 leading-relaxed">{p.excerpt}</p>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-neutral-500 mt-6">{p.read_time}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
